import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const LatestBlogPost = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);

  const lang = i18n.language?.split("-")[0] || "en";

  useEffect(() => {
    fetch(`https://api.whynotcarrental.com/api/blog`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length) setPost(data[0]);
      })
      .catch(() => {});
  }, []);

  if (!post) return null;

  const content = post[lang]?.title ? post[lang] : post.en || {};
  const cover = post.coverImage
    ? `https://api.whynotcarrental.com/uploads/blog/${post.coverImage}`
    : null;

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box sx={{ backgroundColor: "#f4f6fb", py: { xs: 5, md: 8 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" fontWeight={700} color="#1C2541" gutterBottom>
            {t("latestArticle", "Latest Article")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("latestArticleSubtitle", "Stay up to date with the latest news and guides about Madeira.")}
          </Typography>
        </Box>

        {/* Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #e8eaf0",
            overflow: "hidden",
            display: { md: "flex" },
            transition: "box-shadow 0.2s, transform 0.2s",
            "&:hover": {
              boxShadow: "0 8px 32px rgba(28,37,65,0.13)",
              transform: "translateY(-3px)",
            },
          }}
        >
          <CardActionArea
            onClick={() => navigate(`/blog/${post.slug}`)}
            sx={{ display: { md: "flex" }, alignItems: "stretch" }}
          >
            {/* Cover image */}
            {cover && (
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: 340 },
                  minHeight: { xs: 220, md: "auto" },
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={cover}
                  alt={content.title || ""}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    minHeight: { xs: 220, md: 280 },
                    transition: "transform 0.3s",
                    ".MuiCardActionArea-root:hover &": { transform: "scale(1.04)" },
                  }}
                />
              </Box>
            )}

            {/* Content */}
            <CardContent
              sx={{
                flex: 1,
                p: { xs: 2.5, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                {/* Tags */}
                {post.tags?.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {post.tags.slice(0, 3).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: "#EE8013",
                          color: "white",
                          fontWeight: 600,
                          fontSize: 11,
                          height: 22,
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </Stack>
                )}

                {/* Title */}
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#1C2541"
                  gutterBottom
                  sx={{ lineHeight: 1.3 }}
                >
                  {content.title || "Untitled"}
                </Typography>

                {/* Summary */}
                {content.summary && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mt: 1,
                    }}
                  >
                    {content.summary}
                  </Typography>
                )}
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatDate(post.publishedAt)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#EE8013",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {t("readMore", "Read more")}
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Link to full blog */}
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography
            component="button"
            onClick={() => navigate("/blog")}
            sx={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#1C2541",
              fontWeight: 600,
              fontSize: 14,
              textDecoration: "underline",
              "&:hover": { color: "#EE8013" },
            }}
          >
            {t("viewAllArticles", "View all articles")} →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LatestBlogPost;
